import { Button, Modal, notify } from '@affine/component';
import type { DialogComponentProps } from '@affine/core/modules/dialogs';
import type { WORKSPACE_DIALOG_SCHEMA } from '@affine/core/modules/dialogs/constant';
import { NotificationService } from '@affine/core/modules/notification';
import { usePermissionInviteSearchAvailability } from '@affine/core/modules/permissions/views/permission-invite-picker';
import { UserFriendlyError } from '@affine/error';
import {
  type BlockInviteEligibleRecipientsQuery,
  BlockInviteTargetType,
  DocMode as GraphQLDocMode,
} from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useService } from '@toeverything/infra';
import { useCallback, useEffect, useState } from 'react';

import {
  BlockInviteRecipientPicker,
  type EligibleGroup,
  type EligibleUser,
  type SelectedTarget,
  splitSelectedTargetIds,
  targetKey,
} from './recipient-picker';
import * as styles from './styles.css';

type EligibleRecipients =
  BlockInviteEligibleRecipientsQuery['blockInviteEligibleRecipients'];

type EligibleRecipientSelector<TRecipient> = (
  result: EligibleRecipients
) => TRecipient[];

const selectEligibleUsers: EligibleRecipientSelector<EligibleUser> = result =>
  result.users;

const selectEligibleGroups: EligibleRecipientSelector<EligibleGroup> = result =>
  result.groups;

function useBlockInviteEligibleSearch<TRecipient>({
  docId,
  notificationService,
  query,
  selectRecipients,
  workspaceId,
  disabled,
}: {
  workspaceId: string;
  docId: string;
  query: string;
  notificationService: NotificationService;
  selectRecipients: EligibleRecipientSelector<TRecipient>;
  disabled: boolean;
}) {
  const [recipients, setRecipients] = useState<TRecipient[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);

  useEffect(() => {
    const normalizedQuery = query.trim();
    setSearchFailed(false);

    if (!normalizedQuery || disabled) {
      setRecipients([]);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => {
      setIsLoading(true);
      notificationService
        .getBlockInviteEligibleRecipients(
          {
            workspaceId,
            docId,
            query: normalizedQuery,
            take: 20,
          },
          controller.signal
        )
        .then(result => {
          setRecipients(selectRecipients(result));
          setSearchFailed(false);
        })
        .catch(() => {
          if (!controller.signal.aborted) {
            setSearchFailed(true);
            setRecipients([]);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) {
            setIsLoading(false);
          }
        });
    }, 200);

    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    disabled,
    docId,
    notificationService,
    query,
    selectRecipients,
    workspaceId,
  ]);

  return { isLoading, recipients, searchFailed };
}

export const BlockInviteDialog = ({
  close,
  workspaceId,
  docId,
  mode,
  blockId,
  elementId,
  blockPreview,
}: DialogComponentProps<WORKSPACE_DIALOG_SCHEMA['block-invite']>) => {
  const t = useI18n();
  const notificationService = useService(NotificationService);
  const [userSearchText, setUserSearchText] = useState('');
  const [groupSearchText, setGroupSearchText] = useState('');
  const [selectedTargets, setSelectedTargets] = useState<SelectedTarget[]>([]);
  const [isSending, setIsSending] = useState(false);
  const unavailableMessage =
    t['com.streamlain.block-invite.search-unavailable']?.() ??
    'Recipients cannot be searched right now. Check the server connection.';
  const { searchUnavailable } = usePermissionInviteSearchAvailability({
    searchFailed: false,
    unavailableMessage,
  });
  const {
    isLoading: isLoadingUsers,
    recipients: users,
    searchFailed: userSearchFailed,
  } = useBlockInviteEligibleSearch({
    workspaceId,
    docId,
    query: userSearchText,
    notificationService,
    selectRecipients: selectEligibleUsers,
    disabled: searchUnavailable,
  });
  const {
    isLoading: isLoadingGroups,
    recipients: groups,
    searchFailed: groupSearchFailed,
  } = useBlockInviteEligibleSearch({
    workspaceId,
    docId,
    query: groupSearchText,
    notificationService,
    selectRecipients: selectEligibleGroups,
    disabled: searchUnavailable,
  });
  const effectiveSearchFailed = userSearchFailed || groupSearchFailed;
  const {
    searchUnavailable: effectiveSearchUnavailable,
    searchUnavailableMessage: effectiveSearchUnavailableMessage,
  } = usePermissionInviteSearchAvailability({
    searchFailed: effectiveSearchFailed,
    unavailableMessage,
  });

  const selectUser = useCallback((user: EligibleUser) => {
    setSelectedTargets(prev => {
      const target: SelectedTarget = {
        type: BlockInviteTargetType.User,
        value: user,
      };
      const key = targetKey(target);
      return prev.some(item => targetKey(item) === key)
        ? prev.filter(item => targetKey(item) !== key)
        : [...prev, target];
    });
  }, []);

  const selectGroup = useCallback((group: EligibleGroup) => {
    setSelectedTargets(prev => {
      const target: SelectedTarget = {
        type: BlockInviteTargetType.Group,
        value: group,
      };
      const key = targetKey(target);
      return prev.some(item => targetKey(item) === key)
        ? prev.filter(item => targetKey(item) !== key)
        : [...prev, target];
    });
  }, []);

  const removeTarget = useCallback((target: SelectedTarget) => {
    const key = targetKey(target);
    setSelectedTargets(prev => prev.filter(item => targetKey(item) !== key));
  }, []);

  const sendInvite = useCallback(async () => {
    if (selectedTargets.length === 0 || isSending) return;

    setIsSending(true);
    try {
      const { groupIds, userIds } = splitSelectedTargetIds(selectedTargets);

      const result = await notificationService.sendBlockInvite({
        workspaceId,
        docId,
        mode:
          mode === 'edgeless' ? GraphQLDocMode.edgeless : GraphQLDocMode.page,
        blockId: blockId ?? null,
        elementId: elementId ?? null,
        blockPreview: blockPreview ?? null,
        userIds,
        groupIds,
      });

      notify.success({
        title:
          result.createdCount === 1
            ? t['com.streamlain.block-invite.sent-one']()
            : t['com.streamlain.block-invite.sent-many']({
                count: String(result.createdCount),
              }),
      });
      close();
    } catch (error) {
      notify.error(UserFriendlyError.fromAny(error));
    } finally {
      setIsSending(false);
    }
  }, [
    blockId,
    blockPreview,
    close,
    docId,
    elementId,
    isSending,
    mode,
    notificationService,
    selectedTargets,
    t,
    workspaceId,
  ]);

  return (
    <Modal
      open
      onOpenChange={open => {
        if (!open) close();
      }}
      title={t['com.streamlain.block-invite.title']()}
      width={560}
    >
      <div className={styles.content}>
        <BlockInviteRecipientPicker
          groups={groups}
          groupSearchText={groupSearchText}
          isLoadingGroups={isLoadingGroups}
          isLoadingUsers={isLoadingUsers}
          onGroupSearchTextChange={setGroupSearchText}
          onRemoveTarget={removeTarget}
          searchUnavailableMessage={effectiveSearchUnavailableMessage}
          searchUnavailable={effectiveSearchUnavailable}
          onToggleGroup={selectGroup}
          onToggleUser={selectUser}
          onUserSearchTextChange={setUserSearchText}
          selectedTargets={selectedTargets}
          userSearchText={userSearchText}
          users={users}
        />

        <div className={styles.footer}>
          <Button onClick={() => close()}>{t['Cancel']()}</Button>
          <Button
            variant="primary"
            disabled={selectedTargets.length === 0 || (!blockId && !elementId)}
            loading={isSending}
            onClick={() => void sendInvite()}
          >
            {t['com.streamlain.block-invite.send']()}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
